import { useEffect, useRef } from 'react';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { useMediaMatch } from '@storefront/lib-global';
import type { ProductReference } from '@storefront/lib-global/codegen/types';
import { media } from '@storefront/lib-global/stylings';
import { useSizeAndWidthContext } from '../../../context';
import { scrollIntoView } from '../../../utils/scrollIntoView';
import { MeasurementBlockLink, MeasurementList, MeasurementListItem } from '../MeasurementBlock/MeasurementBlock';
import { Sizes } from '../Sizes/Sizes';
import { ErrorMessage } from '../errorMessage/ErrorMessage';
import { SizeContainer } from './SizeSelector.styles';

const SIZE_SCROLL_TOP_MARGIN = -21;

type Props = {
  largerSizeProduct: ProductReference | undefined;
  smallerSizeProduct: ProductReference | undefined;
};

export function SizeSelector({ largerSizeProduct, smallerSizeProduct }: Props) {
  const { t } = useTranslation(['lib-products-common']);
  const { error, uniqueSizes } = useSizeAndWidthContext();
  const sizeSelectorContainer = useRef<HTMLDivElement>(null);
  const isDesktop = useMediaMatch(media.greaterThan('lg'));
  const isOneSizeProduct = uniqueSizes.length === 1;

  useEffect(() => {
    if (error.includes('size')) {
      scrollIntoView(sizeSelectorContainer, isDesktop, SIZE_SCROLL_TOP_MARGIN);
    }
  }, [error, isDesktop]);

  if (uniqueSizes.length < 1) return null;

  return (
    <SizeContainer ref={sizeSelectorContainer} data-testid="sizesContainer">
      <MeasurementList isOneSizeProduct={isOneSizeProduct}>
        {!!smallerSizeProduct && (
          <MeasurementListItem isTwoSpaces>
            <Link
              href={`/${smallerSizeProduct?.targetProduct?.slug}/${smallerSizeProduct?.targetProduct?.key}-p`}
              passHref
            >
              <MeasurementBlockLink title={t('size.selector.smaller.sizes')} data-testid="smallerSizeLink">
                {t('size.selector.smaller.sizes')}
              </MeasurementBlockLink>
            </Link>
          </MeasurementListItem>
        )}
        <Sizes />
        {!!largerSizeProduct && (
          <MeasurementListItem isTwoSpaces>
            <Link
              href={`/${largerSizeProduct?.targetProduct?.slug}/${largerSizeProduct?.targetProduct?.key}-p`}
              passHref
            >
              <MeasurementBlockLink title={t('size.selector.larger.sizes')} data-testid="largerSizeLink">
                {t('size.selector.larger.sizes')}
              </MeasurementBlockLink>
            </Link>
          </MeasurementListItem>
        )}
      </MeasurementList>
      {error.includes('size') ? (
        <ErrorMessage testId="noSizeSelectedError">{t('size.selector.error.message')}</ErrorMessage>
      ) : null}
    </SizeContainer>
  );
}

export default SizeSelector;
